import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ListMusic, Share2, Download, ArrowLeft, Radio, Link as LinkIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

export default function EpisodePage() {
  const { id } = useParams();
  const [ep, setEp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const base = api.defaults.baseURL;

  useEffect(() => {
    api.get(`/episodes/${id}`).then((r) => { setEp(r.data); document.title = `${r.data.title} · In His Image Radio`; }).catch(() => setNotFound(true));
  }, [id]);

  const share = async () => {
    const url = `${window.location.origin}/episodes/${id}`;
    const shareUrl = `${base}/episodes/${id}/share`;
    const text = `🎙️ Listen to "${ep.title}" on In His Image Radio`;
    try {
      if (ep.has_cover && navigator.canShare) {
        try {
          const blob = await (await fetch(`${base}${ep.cover_url}`)).blob();
          const file = new File([blob], "cover.png", { type: blob.type || "image/png" });
          if (navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: ep.title, text, url }); return; }
        } catch (e) { /* fall through */ }
      }
      if (navigator.share) { await navigator.share({ title: ep.title, text, url: shareUrl }); return; }
      await navigator.clipboard.writeText(`${text} ${shareUrl}`);
      toast.success("Link copied — share it with friends!");
    } catch (e) { /* user cancelled */ }
  };
  const copyLink = async () => {
    const shareUrl = `${base}/episodes/${id}/share`;
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied to clipboard 📋"); }
    catch (e) { toast.error("Couldn't copy — long-press the address bar to copy"); }
  };

  const seek = (at) => { const a = document.getElementById("ep-audio"); if (a) { a.currentTime = at || 0; a.play().catch(() => {}); } };

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><p className="text-zinc-400">This episode isn't available.</p><Link to="/live" className="text-[#E6B34A] hover:underline mt-3 inline-block">Back to Live Show</Link></div>;
  if (!ep) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="episode-page">
      <Link to="/live" data-testid="ep-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> Live Show</Link>
      <div className="grid sm:grid-cols-[240px_1fr] gap-6 items-start">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0C1338] aspect-square">
          {ep.has_cover ? <img src={`${base}${ep.cover_url}`} alt={ep.title} className="w-full h-full object-cover" data-testid="ep-cover" /> : <div className="w-full h-full flex items-center justify-center"><Radio className="w-10 h-10 text-zinc-600" /></div>}
        </div>
        <div className="min-w-0">
          <p className="overline text-[#E6B34A]">{ep.series || "Live Broadcast"}</p>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1" data-testid="ep-title">{ep.title}</h1>
          <p className="text-sm text-zinc-500 mt-1">by {ep.host_id ? <Link to={`/hosts/${ep.host_id}`} className="text-[#E6B34A] hover:underline">{ep.host_name}</Link> : ep.host_name}</p>
          {ep.description && <p className="text-sm text-zinc-400 mt-3">{ep.description}</p>}
          <audio id="ep-audio" controls className="w-full mt-4 h-10" data-testid="ep-audio"><source src={`${base}/episodes/${ep.id}/audio`} /></audio>
          <div className="flex gap-2 mt-3">
            <button onClick={share} data-testid="ep-share" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors"><Share2 className="w-4 h-4" /> Share</button>
            <button onClick={copyLink} data-testid="ep-copy-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors"><LinkIcon className="w-4 h-4" /> Copy link</button>
            <a href={`${base}/episodes/${ep.id}/audio?download=1`} download data-testid="ep-download" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors"><Download className="w-4 h-4" /> Download</a>
          </div>
        </div>
      </div>

      {ep.tracks?.length > 0 && (
        <div className="mt-8" data-testid="ep-chapters">
          <p className="overline text-zinc-500 mb-3 flex items-center gap-1.5"><ListMusic className="w-4 h-4 text-[#E6B34A]" /> Chapters — tap to jump</p>
          <div className="space-y-1">
            {ep.tracks.map((t, i) => (
              <button key={i} onClick={() => seek(t.at)} data-testid={`ep-chapter-${i}`} className="w-full flex items-center gap-3 text-left rounded-xl px-3 py-2.5 bg-[#141C42] border border-white/5 hover:border-[#E6B34A]/40 transition-colors">
                <span className="font-mono text-[#E6B34A] tabular-nums shrink-0">{fmt(t.at)}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${t.kind === "sermon" ? "bg-blue-500/15 text-blue-300" : "bg-[#E6B34A]/15 text-[#E6B34A]"}`}>{t.kind === "sermon" ? "Sermon" : "Song"}</span>
                <span className="truncate text-sm">{t.title}{t.artist ? ` — ${t.artist}` : ""}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
